/**
 * Feature 4 (2026-05-28) - TaskFormModal.
 *
 * Create / edit an Agreement Task. Fields:
 *   - description (free text, required)
 *   - deadline (date, required)
 *   - assignee (User picker; non-admins can only pick themselves)
 *
 * Status is NOT edited here - it flips via the dedicated row actions
 * (Mark done / Mark cancelled / Reopen) on the parent tab.
 */
import {
  Button, Group, Modal, SegmentedControl, Select, Stack, Textarea, TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import * as React from 'react';

import { useCurrentUser } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useStaffOptions } from '../hooks/useAgreementInteractions';
import {
  useCreateAgreementTask, useUpdateAgreementTask,
} from '../hooks/useAgreementTasks';
import type { AgreementTask, UUID } from '../api/types';
import { ApiError } from '../api/client';
import { useAlertModal } from './AlertModalProvider';
import { AgreementPicker } from './EntityPicker';
import { DEFAULT_URGENCY, URGENCY_OPTIONS } from '../utils/taskUrgency';

interface FormValues {
  description: string;
  deadline: string;          // YYYY-MM-DD
  urgency: string;           // P14: '1' High · '2' Medium · '3' Low (Select value)
  assignee_user_id: string;
  // Chosen via the AgreementPicker when no fixed agreementId is supplied
  // (create from the Tasks hub). '' otherwise. #63.
  agreement_id: string;
  // #63: 'agreement' shows the picker; 'general' creates a no-agreement task.
  related_to: 'agreement' | 'general';
}

interface Props {
  opened: boolean;
  onClose: () => void;
  // When omitted (e.g. the My/All Tasks hub), the form shows an Agreement
  // picker so the creator chooses the target Agreement. #63.
  agreementId?: UUID;
  editing?: AgreementTask | null;
}

function isoToday(): string {
  // Local-date (not UTC). Matches the overdue-detection helper in
  // AgreementTasksTab so the modal pre-fill and the row badge agree.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function TaskFormModal({ opened, onClose, agreementId, editing }: Props) {
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  // Cross-assignment (assign to someone other than yourself) is gated by the
  // task.assign_others permission (migration 0076; admin + rm + staff by default),
  // not by role — replaces the old isAdmin check.
  const canAssignOthers = can('task.assign_others');
  const create = useCreateAgreementTask();
  const update = useUpdateAgreementTask();
  const { showAlert } = useAlertModal();

  // Reuse the existing staff-options endpoint from Interactions.
  const staffQ = useStaffOptions();
  const assigneeOptions = React.useMemo(() => {
    const raw = staffQ.data;
    const list = Array.isArray(raw) ? raw : [];
    if (canAssignOthers) {
      // Holder of task.assign_others: pick anyone.
      return list.map((u) => ({ value: u.id, label: `${u.display_name} (${u.role})` }));
    }
    // Otherwise: only themselves.
    const self = list.find((u) => u.id === user?.id);
    return self ? [{ value: self.id, label: `${self.display_name} (you)` }] : [];
  }, [staffQ.data, canAssignOthers, user?.id]);

  // Show the Agreement picker only when creating WITHOUT a fixed agreement
  // (the Tasks hub). The agreement is not editable after creation (the PATCH
  // endpoint has no agreement_id), so no picker in edit mode. #63.
  const needsAgreementPicker = !agreementId && !editing;

  const initial: FormValues = editing ? {
    description: editing.description,
    deadline: editing.deadline,
    urgency: String(editing.urgency ?? DEFAULT_URGENCY),
    assignee_user_id: editing.assignee_user_id,
    agreement_id: editing.agreement_id ?? '',
    related_to: editing.agreement_id ? 'agreement' : 'general',
  } : {
    description: '',
    deadline: isoToday(),
    urgency: String(DEFAULT_URGENCY),
    assignee_user_id: user?.id ?? '',
    agreement_id: agreementId ?? '',
    related_to: 'agreement',
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      description: (v) => (v.trim().length > 0 ? null : 'Description is required'),
      deadline: (v) => (v ? null : 'Deadline is required'),
      assignee_user_id: (v) => (v ? null : 'Assignee is required'),
      agreement_id: (v, vals) =>
        (needsAgreementPicker && vals.related_to === 'agreement' && !v
          ? 'Please choose an agreement' : null),
    },
  });

  React.useEffect(() => {
    if (opened) {
      form.setValues(initial);
      form.resetDirty(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editing?.id]);

  function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    // Same Portal-bubble guard as InteractionFormModal: React synthetic
    // events bubble through the component tree even though Mantine
    // Modal renders in a portal; without stopPropagation the outer
    // Agreement form's submit handler also fires.
    e.stopPropagation();
    form.onSubmit(handleSubmit)(e);
  }

  function handleSubmit(values: FormValues) {
    const onError = (err: unknown) => {
      const detail = err instanceof ApiError ? err.detail : String(err);
      showAlert({ color: 'red', title: 'Could not save', message: detail });
    };

    if (editing) {
      update.mutate(
        { id: editing.id, input: {
          description: values.description.trim(),
          deadline: values.deadline,
          urgency: Number(values.urgency),
          assignee_user_id: values.assignee_user_id,
        }},
        {
          onSuccess: () => {
            notifications.show({ message: 'Task updated', color: 'green' });
            onClose();
          },
          onError,
        },
      );
    } else {
      create.mutate(
        {
          agreement_id: agreementId
            ?? (values.related_to === 'general' ? null : values.agreement_id),
          description: values.description.trim(),
          deadline: values.deadline,
          urgency: Number(values.urgency),
          assignee_user_id: values.assignee_user_id,
        },
        {
          onSuccess: () => {
            notifications.show({ message: 'Task created', color: 'green' });
            onClose();
          },
          onError,
        },
      );
    }
  }

  return (
    <Modal opened={opened} onClose={onClose}
      title={editing ? 'Edit task' : 'New task'}
      size="lg" centered>
      <form onSubmit={handleFormSubmit}>
        <Stack>
          {needsAgreementPicker && (
            <Stack gap={4}>
              <SegmentedControl
                data={[
                  { label: 'Agreement', value: 'agreement' },
                  { label: 'General (no agreement)', value: 'general' },
                ]}
                value={form.values.related_to}
                onChange={(v) =>
                  form.setFieldValue('related_to', v as 'agreement' | 'general')}
              />
              {form.values.related_to === 'agreement' && (
                <AgreementPicker
                  label="Agreement"
                  required
                  value={form.values.agreement_id || null}
                  onChange={(id) => form.setFieldValue('agreement_id', id ?? '')}
                  error={form.errors.agreement_id as string | undefined}
                />
              )}
            </Stack>
          )}
          <Textarea label="Task" required autosize minRows={2} maxRows={6}
            maxLength={500}
            placeholder="What needs to be done?"
            {...form.getInputProps('description')} />

          <Group grow>
            <TextInput label="Deadline" type="date" required
              {...form.getInputProps('deadline')} />
            <Select label="Urgency" required allowDeselect={false}
              data={URGENCY_OPTIONS}
              comboboxProps={{ withinPortal: true }}
              {...form.getInputProps('urgency')} />
            <Select label="Assignee" required
              data={assigneeOptions}
              comboboxProps={{ withinPortal: true }}
              description={canAssignOthers
                ? 'You can assign to anyone.'
                : 'You can only assign tasks to yourself.'}
              {...form.getInputProps('assignee_user_id')} />
          </Group>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {editing ? 'Save changes' : 'Create task'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
